<script lang="ts">
	import InputField from '$lib/molecules/InputField.svelte'
	import AutocompleteInputField from '$lib/autocomplete/AutocompleteInputField.svelte'
	import {
		DefaultMaterialProperty,
		DefaultMaterialPropertyListElement,
		dropdownTypes,
		type MaterialProperty,
		materialPropertyDropdownSchema,
		type MaterialPropertyListElement,
		materialPropertyListElementSchema,
		materialPropertySchema,
		MaterialPropertyType,
		PropertySystemFlag
	} from './types/materialProperty'
	import type { FontProps } from '$types/fontProps'
	import { DefaultInputFieldProps, DefaultRobotoFontProps } from '$types/fontProps'
	import type { AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import { AutocompleteConfig } from '$types/autocompleteFieldConfig'
	import { t } from '$i18n/i18n'
	import TextArea from '$lib/atoms/TextArea.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import { getContext, hasContext, untrack } from 'svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import type { Notification } from '$types/notification'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { addNotification } from '$lib/stores'
	import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
	import { writable, type Writable } from 'svelte/store'
	import { mmpvState } from './mmpv.state.svelte'
	import { getFormErrors } from '$utils/formValidation'
	import Button from '$lib/atoms/Button.svelte'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import StaticChipList from '$lib/organisms/StaticChipList.svelte'
	import MaterialPropertyListElements from './MaterialPropertyListElements.svelte'

	const fontProps: FontProps = DefaultInputFieldProps()
	const headerFontProps: FontProps = DefaultRobotoFontProps('15px', '0.875rem')
	const MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX = 'materialProperty.'
	const MATERIAL_FORM_ERROR_PATH_PREFIX = 'material.'

	const materialTypeOptions = Object.values(MaterialPropertyType).map((prop) => ({
		text: $t('UI.mmpv.properties.types.' + prop),
		value: prop
	}))
	let typeAcConfig: AutocompleteFieldConfig = AutocompleteConfig('value', 'text')
	typeAcConfig.clearable = false

	let systemFlagsOptions = $derived.by(() => {
		if (
			[MaterialPropertyType.MULTISELECT, MaterialPropertyType.SINGLESELECT].includes(
				materialProperty.type
			)
		) {
			return Object.values(PropertySystemFlag).map((flag) => ({
				label: $t(`PropertySystemFlag.${flag}`),
				value: flag
			}))
		} else {
			return Object.values(PropertySystemFlag)
				.filter((flag) => flag !== PropertySystemFlag.COMMENT_GENERATION_NEW_LINE_FOR_LIST_ELEMENT)
				.filter((flag) => flag !== PropertySystemFlag.COMMENT_GENERATION_NEW_LINE_FOR_CHILDREN)
				.map((flag) => ({ label: $t(`PropertySystemFlag.${flag}`), value: flag }))
		}
	})

	type MaterialPropertyFormProps = {
		// materialId?: string fixme we probably need this to save the property to an existing material
		materialProperty?: MaterialProperty
		onPushMaterialProperty?: (listElement: MaterialPropertyListElement) => void
		onPushListElementAndCreateNewProperty?: (listElement: MaterialPropertyListElement) => void
	}

	let {
		materialProperty = $bindable(DefaultMaterialProperty() as MaterialProperty),
		onPushMaterialProperty,
		onPushListElementAndCreateNewProperty
	}: MaterialPropertyFormProps = $props()

	// we need to introduce this variable because otherwise the property would be set to null from the AC-component, and then we would get NPEs
	let formErrors: Writable<{ path: string; message: string }[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])
	let selectedMaterialPropertyListElement: MaterialPropertyListElement = $state(
		DefaultMaterialPropertyListElement()
	)

	function updateFlags() {
		if (
			![MaterialPropertyType.MULTISELECT, MaterialPropertyType.SINGLESELECT].includes(
				materialProperty.type
			)
		) {
			const allowedFlags = materialProperty.flags
				.filter(
					(flag: string) => flag !== PropertySystemFlag.COMMENT_GENERATION_NEW_LINE_FOR_LIST_ELEMENT
				)
				.filter(
					(flag: string) => flag !== PropertySystemFlag.COMMENT_GENERATION_NEW_LINE_FOR_CHILDREN
				)
			/**
			 * only write on change: mutating the bound prop invalidates the legacy parent,
			 * which re-fires the `materialProperty.type` effect — an unconditional write of
			 * a fresh array would loop forever (effect_update_depth_exceeded)
			 */
			if (allowedFlags.length !== materialProperty.flags.length) {
				materialProperty.flags = allowedFlags
			}
		}
	}

	/**
	 * Asynchronously saves a material property.
	 * It validates the property as per the relevant schema before saving it.
	 * On successful save, a success notification is added and the property is placed in store.
	 *
	 * @param {MaterialProperty} property - The material property to be saved.
	 * @param {string} target specifies the target of the notification, whether they should be displayed on the modal or not
	 * @param {string} materialId if a materialId is given, we can show another notifcation
	 * @returns {Promise<number>} The ID of the saved property, or void if validation failed.
	 * @throws {Error} If there was an error saving the property.
	 */
	export async function saveMaterialProperty(
		property: MaterialProperty,
		target: string | null,
		materialId: string | null = null
	) {
		try {
			$formErrors = []
			let validatedProperty: MaterialProperty
			if (dropdownTypes.includes(property.type)) {
				validatedProperty = (await materialPropertyDropdownSchema.validate(property, {
					stripUnknown: false,
					abortEarly: false
				})) as MaterialProperty
			} else {
				validatedProperty = (await materialPropertySchema.validate(property, {
					stripUnknown: false,
					abortEarly: false
				})) as MaterialProperty
			}
			if (validatedProperty.priority === null || !validatedProperty.priority) {
				validatedProperty.priority = 100 // set default priority
			}
			await fetchUtils
				.post(`/api/property/update/${property.id}`, validatedProperty)
				.then((data) => {
					let wasNewProperty: boolean = property.id === 'new'
					Object.assign(validatedProperty, data)
					let text: string = wasNewProperty
						? $t('UI.mmpv.material.property.createdSuccessful.notification')
						: $t('UI.mmpv.material.property.updatedSuccessful.notification')
					// if saved property was only/last object in breadcrumbs show notification in material properties page
					if (breadcrumbs.get().length === 1) {
						target = null
						text =
							wasNewProperty && materialId
								? $t('UI.mmpv.material.property.addedSuccessful.notification')
								: $t('UI.mmpv.material.property.updatedSuccessful.notification')
					}
					let notification: Notification = DefaultNotification(
						text,
						NotificationType.SUCCESS,
						true,
						3000,
						target
					)
					addNotification(notification)
					mmpvState.materialProperties.set(validatedProperty.id, validatedProperty) // save new property to store
				})
				.catch((error) => {
					let notification = DefaultNotification(
						error.message ?? error,
						NotificationType.ERROR,
						true,
						0,
						target
					)
					addNotification(notification)
					validatedProperty.id = null
				})
			return validatedProperty.id
		} catch (error) {
			let err = getFormErrors(error, MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX)
			console.warn('ValidationError: ', err)
			$formErrors = err
		}
	}

	/**
	 * Asynchronously creates a property list element.
	 * It validates the property list element as per the relevant schema before creating it.
	 * On successful creation, the property list element is pushed to the material property
	 * and the selected material property list element is reset.
	 *
	 * @throws {Error} If validation failed.
	 */
	async function createPropertyListElement() {
		try {
			$formErrors = []
			let validatedPropertyListElement: MaterialPropertyListElement =
				(await materialPropertyListElementSchema.validate(selectedMaterialPropertyListElement, {
					stripUnknown: false,
					abortEarly: false
				})) as MaterialPropertyListElement
			if (
				validatedPropertyListElement.priority === null ||
				!validatedPropertyListElement.priority
			) {
				validatedPropertyListElement.priority = 100 // set default priority
			}
			/**
			 * reassign the whole bound prop instead of pushing in place: `materialProperty`
			 * arrives via `bind:` from a plain (non-proxied) object, so an in-place
			 * `propertyListElements.push` is not tracked and the new element never renders.
			 */
			materialProperty = {
				...materialProperty,
				propertyListElements: [
					...materialProperty.propertyListElements,
					validatedPropertyListElement
				]
			}
			selectedMaterialPropertyListElement = DefaultMaterialPropertyListElement() // reset property list element
			selectedMaterialPropertyListElement.priority = null
		} catch (error) {
			let err = getFormErrors(error, MATERIAL_FORM_ERROR_PATH_PREFIX)
			console.warn('ValidationError: ', err)
			$formErrors = err
		}
	}

	/**
	 * Removes a property list element from the property.
	 * The function removes the element from both the propertyListElementIds and propertyListElements of the materialProperty.
	 * @param {MaterialPropertyListElement} listElement - The list element to be removed.
	 */
	function removePropertyListElement(listElement: MaterialPropertyListElement) {
		// reassign the bound prop with filtered copies (see createPropertyListElement) so the
		// removal renders; filter returns the list WITHOUT the removed element (splice would
		// return the removed element instead) and does not mutate the original arrays
		materialProperty = {
			...materialProperty,
			propertyListElementIds: materialProperty.propertyListElementIds.filter(
				(id) => id !== listElement.id
			),
			propertyListElements: materialProperty.propertyListElements.filter(
				(element) => element !== listElement
			)
		}
	}

	function pushMaterialProperty(listElement: MaterialPropertyListElement) {
		// we add the material property to the stack, because we navigate to the propertyListElement
		onPushMaterialProperty?.(listElement) // no need to hand over the object, we bind on it anyways
	}

	function pushListElementAndCreateNewProperty(listElement: MaterialPropertyListElement) {
		// we need to push the material Property AND the specific listElement, because we add a new property to the listElement
		onPushListElementAndCreateNewProperty?.(listElement)
	}

	function handleKeydown(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			createPropertyListElement()
		}
	}

	$effect(() => {
		void materialProperty.type
		/**
		 * untrack: legacy `$:` only depended on `materialProperty.type`. updateFlags
		 * also reads `materialProperty.flags`, which it writes itself — tracking that
		 * read would re-fire the effect on its own write.
		 */
		untrack(() => updateFlags())
	})
</script>

<div class="material-property-edit-container">
	<!--{#if allFields} &lt;!&ndash;e.g. for editing an existing property or to create a totally new property &ndash;&gt;-->
	<div style="grid-area: name">
		<InputField
			id="input_property_name"
			label={$t('UI.mmpv.properties.name.label')}
			placeholder={$t('UI.mmpv.properties.name.ph')}
			bind:value={materialProperty.name}
			format={inputFieldFormats.FULL}
			{fontProps}
			{headerFontProps}
			errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX + 'name'}
		/>
	</div>
	<div style="grid-area: flags">
		<StaticChipList
			activeChips={materialProperty.flags}
			list={systemFlagsOptions}
			header="Annotationen"
			fontProps={headerFontProps}
			id="flags"
			chipFontProps={DefaultRobotoFontProps('0.625rem', '0.875rem', undefined, 'white')}
			on:addItem={(e) => {
				materialProperty = {
					...materialProperty,
					flags: [...(materialProperty.flags ?? []), e.detail.selectedItem.value]
				}
			}}
			on:removeItem={(e) => {
				materialProperty = {
					...materialProperty,
					flags: (materialProperty.flags ?? []).filter((f) => f !== e.detail.selectedItem.value)
				}
			}}
		/>
	</div>
	<div style="grid-area: displayName">
		<InputField
			id="input_property_display_name"
			label={$t('UI.mmpv.properties.displayName.label')}
			placeholder={$t('UI.mmpv.properties.displayName.ph')}
			bind:value={materialProperty.displayName}
			format={inputFieldFormats.FULL}
			{fontProps}
			{headerFontProps}
			errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX + 'displayName'}
		/>
	</div>
	<div style="grid-area: priority">
		<InputField
			id="input_property_priority"
			label={$t('UI.mmpv.properties.priority.label')}
			bind:value={materialProperty.priority}
			format={inputFieldFormats.FULL}
			{fontProps}
			{headerFontProps}
			errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX + 'priority'}
			isNumeric
		/>
	</div>
	<div style="grid-area: description">
		<TextArea
			id="textarea_property_description"
			title={$t('UI.mmpv.properties.description.label')}
			placeholder={$t('UI.writeHere.ph')}
			bind:text={materialProperty.description}
			inputFontProps={fontProps}
			{headerFontProps}
			marginTop="0"
			labelGap="0.375rem"
			errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX + 'description'}
		/>
	</div>
	<span class="modal-subheader">{$t('UI.mmpv.materialProperty.type.subheader')}</span>
	<div style="grid-area: type" id="autocomplete-property-type">
		<AutocompleteInputField
			bind:value={materialProperty.type}
			{fontProps}
			autocompleteConfig={typeAcConfig}
			label={$t('UI.mmpv.properties.type.label')}
			options={materialTypeOptions}
			errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX + 'type'}
			hideDropdownArrow={false}
			onChange={(newType: { value: MaterialPropertyType; text: string }) => {
				materialProperty = { ...materialProperty, type: newType.value }
			}}
		/>
	</div>
	{#if dropdownTypes.includes(materialProperty.type)}
		<!-- input field + list of values -->
		<div class="property-values-container">
			<span>{@html $t('UI.mmpv.properties.dropdown.values.hint')}</span>
			<div>
				<div class="property-add-list-element">
					<InputField
						bind:value={selectedMaterialPropertyListElement.name}
						label={$t('UI.mmpv.properties.dropdown.values.label')}
						placeholder={$t('UI.mmpv.materialProperty.createListElement.ph')}
						{fontProps}
						{headerFontProps}
						format={inputFieldFormats.FULL}
						errorPath={MATERIAL_PROPERTY_FORM_ERROR_PATH_PREFIX +
							'propertyListElements' +
							'||' +
							MATERIAL_FORM_ERROR_PATH_PREFIX +
							'name'}
						on:keydown={handleKeydown}
					/>
					<InputField
						bind:value={selectedMaterialPropertyListElement.priority}
						label={$t('UI.mmpv.materialProperty.createListElement.priority.label')}
						placeholder={$t('UI.mmpv.materialProperty.createListElement.priority.ph')}
						{fontProps}
						{headerFontProps}
						format={inputFieldFormats.FULL}
						containerWidth="max-content"
						on:keydown={handleKeydown}
					/>
					<Button variant="primary" onclick={createPropertyListElement}>
						<IconCheckmark width={20} height={20} fill="inherit" />
						<span style="white-space: nowrap; color: inherit"
							>{$t('UI.mmpv.properties.listElements.add')}</span
						>
					</Button>
				</div>

				<MaterialPropertyListElements
					propertyListElements={materialProperty.propertyListElements}
					onSelect={pushMaterialProperty}
					onAddChild={pushListElementAndCreateNewProperty}
					onRemove={removePropertyListElement}
				/>
			</div>
		</div>
	{/if}
</div>

<style>
	@import '$styles/global.scss';

	.material-property-edit-container {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1.25rem 1rem;
		grid-template-areas:
			'name name name name'
			'flags flags flags flags'
			'displayName displayName displayName priority'
			'description description description description'
			'subheader subheader subheader subheader'
			'type type type .'
			'listElements listElements listElements listElements'
			'btns btns btns btns';
		width: 100%;
		align-items: end;

		& > span:first-child {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: var(--primary-green);
		}

		& > span:nth-of-type(2) {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: black;
		}
	}

	span.modal-subheader {
		font-family: Roboto, sans-serif;
		line-height: 21px;
		font-weight: 500;
		font-size: 0.875rem;
		color: var(--color-blue-500);

		grid-area: subheader;
	}

	.property-values-container {
		width: 100%;
		grid-area: listElements;
		margin-top: -0.5rem;

		& > span {
			font-family: Roboto, sans-serif;
			line-height: 15px;
			font-weight: 400;
			font-size: 0.75rem;
			color: black;
		}
	}

	div.property-add-list-element {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		justify-content: flex-start;
		gap: 14px;

		width: 100%;
		margin-top: 1.5rem;
	}

	.material-property-edit-container :global(.sv-control) {
		height: 32px; /* override sv control to have the same height as the usual input fields */
	}

	.material-property-edit-container :global(.checkbox-container) {
		justify-content: flex-start !important; /* override the checkbox to be at the beginning of the row*/
	}
</style>
